/**
 * EmbeddedBrowserRouteGuard — route-level backstop for the shell's embedded
 * Chromium view (EMBEDDED_BROWSER_V1).
 *
 * The pane's own unmount cleanup calls embedded_browser_hide, but the R2
 * drill found a path where the native view stayed painted over the New Task
 * view after a task switch. Navigation here is store-driven (task/layout
 * bypasses the Next router with history.replaceState, so usePathname goes
 * stale on task switches) — this guard therefore watches BOTH the router
 * pathname and the taskStore selection, plus the browser/computer stores,
 * and after every change re-asserts the invariant from outside the pane's
 * lifecycle: live embedded session with no mounted EmbeddedBrowserPane ⇒
 * the shell view is hidden.
 *
 * Checks are deferred (macrotask + a trailing sweep) so the pane's own
 * unmount cleanup for the same navigation runs first — the guard only ever
 * catches what escaped. embedded_browser_hide is idempotent, and the whole
 * guard is inert outside the desktop shell.
 */
"use client";

import { useEffect } from "react";
import { usePathname } from "next/navigation";
import { useTaskStore } from "@/stores/taskStore";
import { useBrowserStore } from "@/stores/browserStore";
import { useComputerStore } from "@/stores/computerStore";
import { isDesktopShell } from "@/lib/desktop-shell";
import {
  embeddedBrowserHide,
  isEmbeddedPaneMounted,
} from "@/lib/embedded-browser";

// Trailing sweep delay — long enough for a navigation's full commit cascade
// (store update → layout re-render → pane unmount) to settle.
const TRAILING_SWEEP_MS = 150;

function hideIfOrphaned(): void {
  // Only a LIVE embedded session can have an orphaned native view; once the
  // session ends the pane unmounts and hides through its own cleanup. The
  // gate keeps the guard from spamming hide on every unrelated store tick.
  if (useBrowserStore.getState().source !== "embedded") return;
  if (!isEmbeddedPaneMounted()) embeddedBrowserHide();
}

export function EmbeddedBrowserRouteGuard() {
  const pathname = usePathname();
  const selectedTaskId = useTaskStore((s) => s.selectedTaskId);

  useEffect(() => {
    if (!isDesktopShell()) return;
    let immediate: ReturnType<typeof setTimeout> | null = null;
    let trailing: ReturnType<typeof setTimeout> | null = null;
    const sweep = () => {
      if (immediate === null) {
        immediate = setTimeout(() => {
          immediate = null;
          hideIfOrphaned();
        }, 0);
      }
      if (trailing === null) {
        trailing = setTimeout(() => {
          trailing = null;
          hideIfOrphaned();
        }, TRAILING_SWEEP_MS);
      }
    };
    // Router navigations + store-driven task switches land via the deps;
    // panel close / computer-tab switches land via the store subscriptions.
    sweep();
    const unsubBrowser = useBrowserStore.subscribe(sweep);
    const unsubComputer = useComputerStore.subscribe(sweep);
    return () => {
      unsubBrowser();
      unsubComputer();
      if (immediate !== null) clearTimeout(immediate);
      if (trailing !== null) clearTimeout(trailing);
    };
  }, [pathname, selectedTaskId]);

  return null;
}
